import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { UserProvider } from './contexts/UserContext';
import ptBR from 'antd/locale/pt_BR';
import Login from './pages/Login';
import AuthToken from './pages/AuthToken';
import SignUp from './pages/SignUp';
import Activation from './pages/Activation';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerFicha from './pages/CustomerFicha';
import OnboardingAlerts from './pages/OnboardingAlerts';
import Players from './pages/Players';
import Games from './pages/Games';
import Instances from './pages/Instances';
import Users from './pages/Users';
import AuditLogs from './pages/AuditLogs';
import Subscriptions from './pages/Subscriptions';
import Monitoring from './pages/Monitoring';
import ChipFailures from './pages/ChipFailures';
import ConvocationDispatch from './pages/ConvocationDispatch';
import Audios from './pages/Audios';
import AiSupport from './pages/AiSupport';
import AtRiskTeams from './pages/AtRiskTeams';
import Payments from './pages/Payments';
import ChurnAnalytics from './pages/ChurnAnalytics';
import Insights from './pages/Insights';
import Reengagement from './pages/Reengagement';
import OrganizerBroadcast from './pages/OrganizerBroadcast';
import StatsAdoption from './pages/StatsAdoption';
import ReceitaAnalytics from './pages/ReceitaAnalytics';
import Overview from './pages/Overview';
import PublicReactivate from './pages/PublicReactivate';
import AdminFeatures from './pages/AdminFeatures';
import AdminBanners from './pages/AdminBanners';
import AdminModeration from './pages/AdminModeration';
import useTrackPageview from './hooks/useTrackPageview';
import AdminLandingPages from './pages/AdminLandingPages';
import CommunityFinance from './pages/admin/CommunityFinance';
import CommunityFinanceHealth from './pages/admin/CommunityFinanceHealth';
import IrregularSubscriptions from './pages/IrregularSubscriptions';
import { TeamConfig, OrganizerPlayers, OrganizerGames, Frequency, MySubscription, Billing, Rankings, OrganizerTeamLineup } from './pages/organizer';
import TeamSelector from './pages/organizer/TeamSelector';
import TeamCreate from './pages/organizer/TeamCreate';
import Charges from './pages/organizer/Charges';
import Referrals from './pages/organizer/Referrals';
import Synchronize from './pages/organizer/Synchronize';
import Help from './pages/organizer/Help';
import TeamOrganizers from './pages/organizer/TeamOrganizers';
import MyAccount from './pages/organizer/MyAccount';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Affiliates from './pages/Affiliates';
import Commissions from './pages/Commissions';
import Metrics from './pages/Metrics';
import CoverageMap from './pages/CoverageMap';
import PlayersCoverageMap from './pages/PlayersCoverageMap';
import MessageLogs from './pages/MessageLogs';
import Convocations from './pages/Convocations';

// Community pages
import PlayerRegister from './pages/community/PlayerRegister';
import PlayerLogin from './pages/community/PlayerLogin';
import PlayerDashboard from './pages/community/PlayerDashboard';
import PlayerProfile from './pages/community/PlayerProfile';
import TeamDashboard from './pages/community/TeamDashboard';
import TeamCommunity from './pages/community/TeamCommunity';
import TeamEdit from './pages/community/TeamEdit';
import TeamInviteHistory from './pages/community/TeamInviteHistory';
import CommunityMap from './pages/community/CommunityMap';
import OrganizerMap from './pages/community/OrganizerMap';
import CommunityLayout from './pages/community/CommunityLayout';
import PlayerReferrals from './pages/community/PlayerReferrals';
import GameRating from './pages/GameRating';
import PlayerRatings from './pages/community/PlayerRatings';
import InvitesPage from './pages/community/InvitesPage';
import Procurar from './pages/community/Procurar';
import TeamOwnerSetup from './pages/community/TeamOwnerSetup';
import TeamOwnerDashboard from './pages/community/TeamOwnerDashboard';
import TeamOwnerUpgrade from './pages/community/TeamOwnerUpgrade';
import TeamOwnerLayout from './pages/community/TeamOwnerLayout';
import TeamRoster from './pages/community/TeamRoster';
import TeamLineup from './pages/community/TeamLineup';
import PlayerAchievements from './pages/community/PlayerAchievements';
import TeamOwnerInvites from './pages/community/TeamOwnerInvites';
import TeamOwnerEdit from './pages/community/TeamOwnerEdit';
import TeamOwnerFindPlayers from './pages/community/TeamOwnerFindPlayers';
import FeedPage from './pages/community/FeedPage';
import FriendlyMatches from './pages/community/FriendlyMatches';
import MyFriendlies from './pages/community/MyFriendlies';
import TeamOwnerMatchesPage from './pages/community/TeamOwnerMatchesPage';
import ConvocationDetailPage from './pages/community/ConvocationDetailPage';
import PublicConvocationPage from './pages/community/PublicConvocationPage';
import CommunityFinancePage from './pages/community/finance';
import BatchDetailPage from './pages/community/finance/charges/BatchDetailPage';
import AllBatchesPage from './pages/community/finance/charges/AllBatchesPage';
import FinanceExtratoPage from './pages/community/finance/FinanceExtratoPage';
import FinanceSettingsPage from './pages/community/finance/FinanceSettingsPage';
import FinanceContactsPage from './pages/community/finance/FinanceContactsPage';
import NewChargeBatchWizard from './pages/community/finance/charges/NewChargeBatchWizard';
import RecurrencesPage from './pages/community/finance/charges/RecurrencesPage';
import { getActiveTeamId as getActiveTeamIdForCharges } from './utils/activeTeam';
import AdminCommunityMural from './pages/community/admin/AdminCommunityMural';
import AdminCommunityFriendlies from './pages/community/admin/AdminCommunityFriendlies';
import AdminCommunityMatches from './pages/community/admin/AdminCommunityMatches';
import AdminCommunityRoster from './pages/AdminCommunityRoster';
import PublicTeamProfile from './pages/community/PublicTeamProfile';
import TermsPage from './pages/community/TermsPage';
import TeamCommunityLanding from './pages/community/TeamCommunityLanding';
import CommunityRootLanding from './pages/community/CommunityRootLanding';
import PublicPlayerProfile from './pages/community/PublicPlayerProfile';
import PublicPlayerRegister from './pages/public/PublicPlayerRegister';
import HowRankingWorks from './pages/public/HowRankingWorks';
import HowRatingWorks from './pages/public/HowRatingWorks';
import PayCharge from './pages/public/PayCharge';

import 'antd/dist/reset.css';
import './styles/mobile.css';

// Wrapper que roda useTrackPageview dentro do Router — hook precisa de
// useLocation. So dispara em hostname comunidade (guard interno).
function PageviewTracker() {
  useTrackPageview();
  return null;
}

const theme = {
  token: {
    colorPrimary: '#25D366',
    colorSuccess: '#52C41A',
    colorInfo: '#00B96B',
    borderRadius: 12,
    fontFamily: 'Inter, sans-serif',
  },
  components: {
    Button: { primaryShadow: 'none', defaultShadow: 'none' },
    Card: { borderRadiusLG: 16 },
    Tabs: { itemActiveColor: '#128C7E', inkBarColor: '#25D366' },
  },
};

function NewChargeBatchWizardWrapper() {
  const teamId = getActiveTeamIdForCharges();
  return <NewChargeBatchWizard communityTeamId={teamId} />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp < currentTime) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        } else {
          setUser(JSON.parse(userData));
        }
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <ConfigProvider locale={ptBR} theme={theme}>
      <Router>
        <PageviewTracker />
        <Routes>
          {/* Comunidade v3: no dominio comunidade.futezap.com.br, raiz mostra
              landing com escolha "Sou jogador" ou "Sou dono de time". No
              admin.futezap.com.br continua comportamento antigo (Login abaixo). */}
          {typeof window !== 'undefined' && window.location.hostname === 'comunidade.futezap.com.br' && (
            <Route path="/" element={<CommunityRootLanding />} />
          )}
          <Route path="/auth-tk" element={<AuthToken onLogin={handleLogin} />} />
          <Route path="/cadastro" element={<SignUp />} />
          <Route path="/ativacao" element={<Activation />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/avaliar/:token" element={<GameRating />} />
          <Route path="/cadastro/jogador/:code" element={<PublicPlayerRegister />} />
          <Route path="/reativar/:code" element={<PublicReactivate />} />
          <Route path="/comunidade/pagar/:token" element={<PayCharge />} />
          <Route path="/c/:token" element={<PublicConvocationPage />} />
          <Route path="/como-funciona-ranking" element={<HowRankingWorks />} />
          <Route path="/como-avaliar" element={<HowRatingWorks />} />

          {/* Player Routes - Always accessible */}
          <Route path="/player/register" element={<PlayerRegister />} />
          <Route path="/player/login" element={<PlayerLogin />} />
          <Route path="/player/dashboard" element={<CommunityLayout userType="player"><PlayerDashboard /></CommunityLayout>} />
          <Route path="/player/profile" element={<CommunityLayout userType="player"><PlayerProfile /></CommunityLayout>} />
          <Route path="/player/referrals" element={<CommunityLayout userType="player"><PlayerReferrals /></CommunityLayout>} />
          <Route path="/player/ratings" element={<CommunityLayout userType="player"><PlayerRatings /></CommunityLayout>} />
          <Route path="/player/conquistas" element={<CommunityLayout userType="player"><PlayerAchievements /></CommunityLayout>} />
          <Route path="/player/map" element={<CommunityLayout userType="player"><CommunityMap /></CommunityLayout>} />
          <Route path="/player/invites" element={<CommunityLayout userType="player"><InvitesPage userType="player" /></CommunityLayout>} />
          <Route path="/player/procurar" element={<CommunityLayout userType="player"><Procurar /></CommunityLayout>} />
          <Route path="/player/feed" element={<CommunityLayout userType="player"><FeedPage /></CommunityLayout>} />

          {/* Comunidade v3: time-comunidade autonomo (dono = community_player) */}
          {/* /time (raiz) = landing publica do time-comunidade.
              /jogador = alias amigavel pra /player/login.
              Ambos funcionam nos 2 dominios; no admin.futezap sao ignorados
              (usuario admin nao digita essas URLs). Nao afeta rotas /player/* existentes. */}
          <Route path="/time" element={<TeamCommunityLanding />} />
          <Route path="/jogador" element={<Navigate to="/player/login" replace />} />
          <Route path="/termos" element={<TermsPage />} />
          <Route path="/time/setup" element={<TeamOwnerLayout><TeamOwnerSetup /></TeamOwnerLayout>} />
          <Route path="/time/dashboard/:id" element={<TeamOwnerLayout><TeamOwnerDashboard /></TeamOwnerLayout>} />
          <Route path="/time/upgrade/:id" element={<TeamOwnerLayout><TeamOwnerUpgrade /></TeamOwnerLayout>} />
          <Route path="/time/mapa" element={<TeamOwnerLayout><CommunityMap /></TeamOwnerLayout>} />
          <Route path="/time/procurar" element={<TeamOwnerLayout><TeamOwnerFindPlayers /></TeamOwnerLayout>} />
          <Route path="/time/convites" element={<TeamOwnerLayout><TeamOwnerInvites /></TeamOwnerLayout>} />
          <Route path="/time/editar/:id" element={<TeamOwnerLayout><TeamOwnerEdit /></TeamOwnerLayout>} />
          <Route path="/time/elenco" element={<TeamOwnerLayout><TeamRoster /></TeamOwnerLayout>} />
          <Route path="/time/escalacao" element={<TeamOwnerLayout><TeamLineup /></TeamOwnerLayout>} />
          <Route path="/time/feed" element={<TeamOwnerLayout><FeedPage /></TeamOwnerLayout>} />
          <Route path="/time/amistosos" element={<TeamOwnerLayout><FriendlyMatches /></TeamOwnerLayout>} />
          <Route path="/time/meus-amistosos" element={<TeamOwnerLayout><MyFriendlies /></TeamOwnerLayout>} />
          <Route path="/time/jogos" element={<TeamOwnerLayout><TeamOwnerMatchesPage /></TeamOwnerLayout>} />
          <Route path="/time/jogos/:id" element={<TeamOwnerLayout><TeamOwnerMatchesPage /></TeamOwnerLayout>} />
          <Route path="/time/convocacao/:id" element={<TeamOwnerLayout><ConvocationDetailPage /></TeamOwnerLayout>} />
          <Route path="/comunidade/financeiro" element={<TeamOwnerLayout><CommunityFinancePage /></TeamOwnerLayout>} />
          <Route path="/comunidade/financeiro/lote/:id" element={<TeamOwnerLayout><BatchDetailPage /></TeamOwnerLayout>} />
          <Route path="/comunidade/financeiro/nova-cobranca" element={<TeamOwnerLayout><NewChargeBatchWizardWrapper /></TeamOwnerLayout>} />
          <Route path="/comunidade/financeiro/cobrancas" element={<TeamOwnerLayout><AllBatchesPage /></TeamOwnerLayout>} />
          <Route path="/comunidade/financeiro/recorrencias" element={<TeamOwnerLayout><RecurrencesPage /></TeamOwnerLayout>} />
          <Route path="/comunidade/financeiro/extrato" element={<TeamOwnerLayout><FinanceExtratoPage /></TeamOwnerLayout>} />
          <Route path="/comunidade/financeiro/contatos" element={<TeamOwnerLayout><FinanceContactsPage /></TeamOwnerLayout>} />
          <Route path="/comunidade/financeiro/configuracoes" element={<TeamOwnerLayout><FinanceSettingsPage /></TeamOwnerLayout>} />
          <Route path="/player/amistosos" element={<CommunityLayout userType="player"><FriendlyMatches /></CommunityLayout>} />

          {/* Perfil publico do TIME por slug — SOMENTE no dominio comunidade.
              /time/:slug (declarada antes de /:slug pra ser matcheada primeiro
              apenas quando o hostname eh comunidade). */}
          {typeof window !== 'undefined' && window.location.hostname === 'comunidade.futezap.com.br' && (
            <Route path="/time/:slug" element={<PublicTeamProfile />} />
          )}

          {/* Perfil publico do jogador — nova URL /jogador/:slug (2026-08-22).
              Rota antiga /:slug fica como fallback redirect (links antigos
              compartilhados continuam funcionando).
              SOMENTE no dominio comunidade. */}
          {typeof window !== 'undefined' && window.location.hostname === 'comunidade.futezap.com.br' && (
            <>
              <Route path="/jogador/:slug" element={<PublicPlayerProfile />} />
              <Route path="/:slug" element={<PublicPlayerProfile legacyRedirect />} />
            </>
          )}

          {/* Admin Routes */}
          {!user ? (
            <Route path="*" element={<Login onLogin={handleLogin} />} />
          ) : (
            <Route path="*" element={
              <UserProvider user={user}>
                <Layout onLogout={handleLogout}>
                  <Routes>
                    <Route path="/" element={<Navigate to={user?.profile === 'ORGANIZADOR' ? '/team-config' : '/dashboard'} />} />
                    
                    {user?.profile === 'SUPORTE' && (
                      <>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/customers" element={<Customers />} />
                        <Route path="/customers/:id/ficha" element={<CustomerFicha />} />
                        <Route path="/monitoring/onboarding-alerts" element={<OnboardingAlerts />} />
                        <Route path="/monitoring/chip-failures" element={<ChipFailures />} />
                        <Route path="/monitoring/convocation-dispatch" element={<ConvocationDispatch />} />
                        <Route path="/monitoring/expired-teams" element={<Navigate to="/monitoring/irregular-subscriptions?status=PLANO_EXPIRADO" replace />} />
                        <Route path="/monitoring/irregular-subscriptions" element={<IrregularSubscriptions />} />
                        <Route path="/monitoring/receita-analytics" element={<ReceitaAnalytics />} />
                        <Route path="/monitoring/overview" element={<Overview />} />
                        <Route path="/monitoring/moderacao" element={<AdminModeration />} />
                        <Route path="/players" element={<Players />} />
                        <Route path="/games" element={<Games />} />
                        <Route path="/instances" element={<Instances />} />
                        <Route path="/subscriptions" element={<Subscriptions />} />
                        <Route path="/monitoring" element={<Monitoring />} />
                        <Route path="/affiliates" element={<Affiliates />} />
                        <Route path="/commissions" element={<Commissions />} />
                        <Route path="/message-logs" element={<MessageLogs />} />
                        <Route path="/convocations" element={<Convocations />} />
                        <Route path="/help" element={<Help />} />
                      </>
                    )}

                    {user?.profile === 'ADMINISTRADOR' && (
                      <>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/metrics" element={<Metrics />} />
                        <Route path="/coverage-map" element={<CoverageMap />} />
                        <Route path="/players-coverage-map" element={<PlayersCoverageMap />} />
                        <Route path="/customers" element={<Customers />} />
                        <Route path="/customers/:id/ficha" element={<CustomerFicha />} />
                        <Route path="/players" element={<Players />} />
                        <Route path="/games" element={<Games />} />
                        <Route path="/instances" element={<Instances />} />
                        <Route path="/subscriptions" element={<Subscriptions />} />
                        <Route path="/monitoring/onboarding-alerts" element={<OnboardingAlerts />} />
                        <Route path="/monitoring/chip-failures" element={<ChipFailures />} />
                        <Route path="/monitoring/convocation-dispatch" element={<ConvocationDispatch />} />
                        <Route path="/monitoring/expired-teams" element={<Navigate to="/monitoring/irregular-subscriptions?status=PLANO_EXPIRADO" replace />} />
                        <Route path="/monitoring/irregular-subscriptions" element={<IrregularSubscriptions />} />
                        <Route path="/users" element={<Users />} />
                        <Route path="/audit-logs" element={<AuditLogs />} />
                        <Route path="/monitoring" element={<Monitoring />} />
                        <Route path="/affiliates" element={<Affiliates />} />
                        <Route path="/commissions" element={<Commissions />} />
                        <Route path="/message-logs" element={<MessageLogs />} />
                        <Route path="/convocations" element={<Convocations />} />
                        <Route path="/audios" element={<Audios />} />
                        <Route path="/monitoring/ai-support" element={<AiSupport />} />
                        <Route path="/monitoring/at-risk" element={<AtRiskTeams />} />
                        <Route path="/monitoring/payments" element={<Payments />} />
                        <Route path="/monitoring/churn" element={<ChurnAnalytics />} />
                        <Route path="/monitoring/insights" element={<Insights />} />
                        <Route path="/monitoring/reengagement" element={<Reengagement />} />
                        <Route path="/monitoring/organizer-broadcast" element={<OrganizerBroadcast />} />
                        <Route path="/monitoring/stats-adoption" element={<StatsAdoption />} />
                        <Route path="/monitoring/receita-analytics" element={<ReceitaAnalytics />} />
                        <Route path="/monitoring/overview" element={<Overview />} />
                        <Route path="/monitoring/moderacao" element={<AdminModeration />} />
                        <Route path="/admin/features" element={<AdminFeatures />} />
                        <Route path="/admin/banners" element={<AdminBanners />} />
                        <Route path="/admin/landing-pages" element={<AdminLandingPages />} />
                        <Route path="/admin/cobranca-comunidade" element={<CommunityFinance />} />
                        <Route path="/admin/comunidade/financeiro-health" element={<CommunityFinanceHealth />} />
                        <Route path="/team-config" element={<TeamConfig />} />
                        <Route path="/organizer-players" element={<OrganizerPlayers />} />
                        <Route path="/organizer-games" element={<OrganizerGames />} />
                        <Route path="/escalacao" element={<OrganizerTeamLineup />} />
                        <Route path="/rankings" element={<Rankings />} />
                        <Route path="/my-subscription" element={<MySubscription />} />
                        <Route path="/help" element={<Help />} />
                      </>
                    )}
                    
                    {user?.profile === 'ORGANIZADOR' && (
                      <>
                        <Route path="/team-config" element={<TeamConfig />} />
                        <Route path="/organizer/teams" element={<TeamSelector />} />
                        <Route path="/organizer/teams/new" element={<TeamCreate />} />
                        <Route path="/organizer-players" element={<OrganizerPlayers />} />
                        <Route path="/organizer-games" element={<OrganizerGames />} />
                        <Route path="/escalacao" element={<OrganizerTeamLineup />} />
                        <Route path="/rankings" element={<Rankings />} />
                        <Route path="/frequency" element={<Frequency />} />
                        <Route path="/cobrancas" element={<Charges />} />
                        <Route path="/charges" element={<Navigate to="/cobrancas" replace />} />
                        <Route path="/team-organizers" element={<TeamOrganizers />} />
                        <Route path="/referrals" element={<Referrals />} />
                        <Route path="/my-subscription" element={<MySubscription />} />
                        <Route path="/my-account" element={<MyAccount />} />
                        <Route path="/sincronizar" element={<Synchronize />} />
                        <Route path="/community/team" element={<TeamDashboard />} />
                        <Route path="/community/team/profile" element={<TeamCommunity />} />
                        <Route path="/community/team/edit" element={<TeamEdit />} />
                        <Route path="/community/invites/history" element={<TeamInviteHistory />} />
                        <Route path="/community/invites" element={<InvitesPage userType="organizer" />} />
                        <Route path="/community/map" element={<OrganizerMap />} />
                        <Route path="/community/mural" element={<AdminCommunityMural />} />
                        <Route path="/community/elenco" element={<AdminCommunityRoster />} />
                        <Route path="/community/amistosos" element={<AdminCommunityFriendlies />} />
                        <Route path="/community/jogos" element={<AdminCommunityMatches />} />
                        <Route path="/help" element={<Help />} />
                      </>
                    )}}
                  </Routes>
                </Layout>
              </UserProvider>
            } />
          )}
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;